import * as muiColors from "@mui/material/colors"


// The one place where colours live. Nothing outside this file writes a colour value.

export const rentacarPalette = {
	// The app blue (`dsColorNew.appBlue` and its tones), so every MUI piece that takes the
	// theme's primary — buttons, checkboxes, switches, tabs, focused fields, the date
	// picker — paints the same blue as the restyled screens (it was #3388b5)
	primary: {
		darker: '#1D4ED8',
		dark: '#3d7bc4',
		main: '#4a90d9',
		light: '#BFDBFE',
		lighter: '#EBF2FC'
	},

	secondary: {
		lighter: '#dcdcdc',
		light: '#6f6f6f',
		main: '#4b4b4b',
		dark: '#0f0f0f',
		darker: '#1e1e1e',
		contrastText: '#f9fafb'
	},

	info: {
		main: '#1890ff'
	},

	warning: {
		main: '#ffb236',
		light: '#ffe08a',
		lighter: '#fff3c2'
	},

	success: {
		light: '#a3d87c',
		lighter: '#b9e6a9'
	},

	error: {
		light: '#f28b82',
		lighter: '#f6a5a5'
	},

	background: {
		grey: '#eeeeee',
		lightGrey: '#f5f5f5'
	},

	colors: {
		red: '#ef5350',
		pink: '#ec407a',
		purple: '#ab47bc',
		deepPurple: '#7e57c2',
		indigo: '#5c6bc0',
		blue: '#42a5f5',
		lightBlue: '#29b6f6',
		cyan: '#26c6da',
		teal: '#26a69a',
		green: '#66bb6a',
		lightGreen: '#9ccc65',
		lime: '#d4e157',
		yellow: '#ffee58',
		amber: '#ffca28',
		orange: '#ffa726',
		deepOrange: '#ff7043',
		brown: '#8d6e63',
		grey: '#bdbdbd',
		blueGrey: '#78909c'
	}
}

// Corporate restyle tokens (Dataseekers shell, curated in dataseekers-retail-kit)
export const dsColorNew = {
	navy: '#0C1A3E',
	// `alpha(white, .08)` and `alpha(white, .14)` flattened on `navy`: the menu's raised fill and
	// its hover, opaque because the gradient-ring trick (padding-box over border-box) needs one
	navyRaised: '#1F2C4D',
	navyRaisedHover: '#2A3A5E',
	appBlue: '#4a90d9',
	appBlueHover: '#3d7bc4',
	webBlue500: '#2563EB',
	webBlueHover: '#1549CC',
	cyan: '#06B6D4',
	cyanHover: '#0591B2',
	grayBorder: '#E5E7EB',
	hoverBgLight: '#F0F4FF',
	textDark: '#374151',
	textTitle: '#111827',
	textMuted: '#6B7280',
	contentBg: '#F5F7FA',
	filterInputBg: '#F9FAFB',
	loginFormBg: '#F0F2F5',
	black: '#000000',
	textSubtle: '#9CA3AF',
	tableHead: '#2E3F66',
	pillTrackBg: '#F3F4F6',
	pillActiveBg: '#EBF2FC',
	tableRowAlt: '#FAFBFC',
	white: '#FFFFFF',
	// destructive actions, notes and add/remove marks (selection bar, bulk-edit dialogs)
	dangerRed: '#DC2626',
	dangerBorder: '#FECACA',
	dangerBg: '#FEF2F2',
	dangerSoft: '#F87171',
	successText: '#15803D',
	successBg: '#ECFDF5',
	noteAmber: '#B45309',
	noteAmberBg: '#FFFBEB',
	noteAmberBorder: '#FDE68A',
	noteInfo: '#1D4ED8',
	noteInfoBg: '#EFF6FF',
	noteInfoBorder: '#BFDBFE',
	// accent strips of the notices (toasts)
	successBar: '#22C55E',
	warningBar: '#F59E0B',
}

// MUI colour scales, for the tones screens used to write by hand (red[400], green[800]...)
export const shades = muiColors

// Values that are neither corporate tokens nor MUI tones, kept exactly as screens used them
export const legacyColors = {
	warningDefault: '#ed6c02',
	grey333: '#333333',
	grey555: '#555555',
	borderFaint: '#cccccc66',
	cssBlue: 'blue',
	cssGrey: 'grey',
	cssGray: 'gray',
	cssGreen: 'green',
}

// Made-up competitor brands shown in the guided tours' demo data
export const demoBrandColors = {
	rentalSpot: '#007BC1',
	bookRide: '#00A651',
	priceFly: '#FF690F',
	swiftFleet: '#0770E3',
	drivePro: '#1A6B37',
	speedCar: '#FFD700',
	autoLink: '#CC0000',
}

// Tailwind tones and a few tints My Business uses for chart series, heat scales and tiers, kept exactly as written
export const dataColors = {
	// lines of the price curve of a date (CU-869fmjzrg UX D2, T10): never red or green; same order as SERIES_COLORS
	series1: '#7C3AED',
	series2: '#CA8A04',
	series3: '#0891B2',
	series4: '#C026D3',
	series5: '#EA580C',
	series6: '#57534E',
	series7: '#78350F',
	series8: '#1F2937',
	series9: '#DB2777',
	sky500: '#0EA5E9',
	emerald500: '#10B981',
	green100: '#DCFCE7',
	green200: '#BBF7D0',
	green300: '#86EFAC',
	green600: '#16A34A',
	green800: '#166534',
	green900: '#14532D',
	mint200: '#C7F5D6',
	mint300: '#A7EBC0',
	red100: '#FEE2E2',
	red300: '#FCA5A5',
	red500: '#EF4444',
	red700: '#B91C1C',
	red800: '#991B1B',
	red900: '#7F1D1D',
	orange300: '#FDBA74',
	violet500: '#8B5CF6',
	violet600: '#7C3AED',
	violet700: '#6D28D9',
	pink500: '#EC4899',
	cyanSoft: '#8FD8EA',
	slate100: '#F1F5F9',
	slate200: '#E2E8F0',
	slate400: '#94A3B8',
	slate500: '#64748B',
	slate600: '#475569',
	slate800: '#1E293B',
	gray300: '#D1D5DB',
	gray600: '#4B5563',
	// table lines and the day labels on the navy calendar heads
	tableLine: '#EEF0F3',
	tableLineFaint: '#F1F2F4',
	onNavyMuted: '#C9D3E6',
}
